// src/components/opportunity/pdf/generateProposalPdf.ts
//
// Entry point that lazy-imports `@react-pdf/renderer` + the proposal
// document, resolves the org logo to a Blob/data URL with the SVG
// fallback the plan calls for, renders the PDF to a Blob, and uploads
// it via the sign+commit flow.
//
// This module is the only place that does the logo fetch; everywhere
// else just calls into here. Keeping it isolated also keeps the
// `@react-pdf/renderer` bundle out of the main chunk.

import type {
  Opportunity,
  OpportunityPricingSnapshot,
  OpportunityRevision,
} from "@/types/opportunity";
import type { AuthProfile } from "@/hooks/authStateModel";
import type { RepContactLocation } from "@/lib/opportunity/fetchRepContactLocation";
import { uploadGeneratedProposalPdf } from "@/lib/opportunity/opportunityGeneratedClient";
import { resolveLogoSrc, revokeIfBlobUrl } from "./pdfLogo";
import { resolveProposalSectionsForPackage } from "@/lib/opportunity/proposalPageResolver";
import { resolveManagerPresetFormatKey } from "@/lib/opportunity/formatRegistry";
import { buildProposalManifest, docDocumentIdsFromPages } from "@/lib/opportunity/proposalAlternateApply";
import { resolveSelectedProposalAlternate } from "@/lib/opportunity/proposalAlternateClient";
import { listProposalPages } from "@/lib/opportunity/proposalPageClient";
import { loadProposalEmbeddedDocPages } from "@/lib/opportunity/proposalDocPageLoader";
import { resolveFormatSettings } from "@/lib/opportunity/formatSettingsResolver";
import { resolveFormatCalcLines } from "@/lib/opportunity/formatCalcLineResolver";
import { evaluateFormatCalcLines } from "@/lib/opportunity/formatCalcEval";
import {
  buildPaymentLineAmountMap,
  evaluatePaymentMilestones,
} from "@/lib/opportunity/formatPaymentMilestoneEval";
import { resolveFormatPaymentMilestones } from "@/lib/opportunity/formatPaymentMilestoneResolver";
import {
  resolveFormatProposalContactSettings,
  resolveProposalContact,
} from "@/lib/opportunity/proposalContactResolver";
import type { OrgProposalContactRow } from "@/lib/opportunity/proposalContactTypes";
import {
  outputTypeForArchetype,
  type OpportunityDocumentArchetype,
} from "@/lib/opportunity/documentArchetypes";
import type { ProposalAlternate } from "@/lib/opportunity/proposalAlternateTypes";
import { pdfRenderErrorMessage } from "./pdfChunkLoadError";

export type GenerateProposalArgs = {
  opportunity: Opportunity;
  snapshot: OpportunityPricingSnapshot;
  revision: OpportunityRevision | null;
  brandName: string;
  organizationName: string;
  tagline: string | null;
  logoUrl: string | null;
  proposalContactRow: OrgProposalContactRow;
  profile: AuthProfile | null;
  activeLocation: RepContactLocation | null;
  /** When omitted, resolves default alternate for the product. */
  alternateKey?: string | null;
  /** Package family; defaults to 'proposal'. Contract packages render
   * the contract-archetype manifest (Standard = signature page only,
   * plus manager-attached Document Builder pages). */
  archetype?: OpportunityDocumentArchetype;
  /**
   * Manager preview: render only this library page as a one-page package.
   * When set, `alternateKey` is ignored for manifest selection.
   */
  previewPageId?: string | null;
};

export type GenerateProposalResult =
  | { ok: true; outputId: string; signedUrl?: string }
  | { ok: false; error: string };

export type RenderProposalResult =
  | { ok: true; blob: Blob; alternateKey: string; alternateLabel: string }
  | { ok: false; error: string };

/**
 * Renders the proposal PDF to a Blob without uploading anywhere. Used
 * by the Preview modal so the user can review the document in an
 * iframe before committing to a saved revision. The same Blob can
 * then be handed to `uploadRenderedProposalPdf` to persist without a
 * re-render (cheap follow-up — the cost is in the rendering, not the
 * upload).
 */
export async function renderProposalPdfToBlob(
  args: GenerateProposalArgs,
): Promise<RenderProposalResult> {
  let logoSrc: string | null = null;
  let embeddedAssetSrcs: string[] = [];

  try {
    // Step 1: fetch + classify the logo, and in parallel resolve the
    // org's proposal text overrides for this snapshot's (format_key,
    // product_type). Both are best-effort: a logo failure → text
    // fallback; a text-resolver failure → hard-coded defaults.
    const presetFormatKey = resolveManagerPresetFormatKey(args.opportunity.product_type);
    const [
      resolvedLogo,
      formatCalcLines,
      formatSettings,
      paymentMilestones,
      formatContact,
      alternateResolved,
    ] = await Promise.all([
      resolveLogoSrc(args.logoUrl),
      resolveFormatCalcLines({
        formatKey: presetFormatKey,
        productType: args.opportunity.product_type,
      }),
      resolveFormatSettings({
        formatKey: presetFormatKey,
        productType: args.opportunity.product_type,
      }),
      resolveFormatPaymentMilestones({
        formatKey: presetFormatKey,
        productType: args.opportunity.product_type,
      }),
      resolveFormatProposalContactSettings({
        formatKey: presetFormatKey,
        productType: args.opportunity.product_type,
      }),
      resolveSelectedProposalAlternate({
        productType: args.opportunity.product_type,
        alternateKey: args.alternateKey,
        archetype: args.archetype ?? "proposal",
      }),
    ]);
    logoSrc = resolvedLogo;
    const pagePreview = await resolvePreviewPageAlternate({
      formatKey: presetFormatKey,
      productType: args.opportunity.product_type,
      previewPageId: args.previewPageId,
      fallback: alternateResolved.selected,
    });
    if (!pagePreview.ok) return { ok: false, error: pagePreview.error };
    const selectedAlternate = pagePreview.alternate;
    const resolved = await resolveProposalSectionsForPackage({
      formatKey: presetFormatKey,
      productType: args.opportunity.product_type,
      pages: selectedAlternate.pages,
    });
    // applyAlternatePagesToSections is already applied inside the page resolver.
    const docIds = docDocumentIdsFromPages(selectedAlternate.pages);
    const embeddedRes = await loadProposalEmbeddedDocPages(docIds);
    if (!embeddedRes.ok) return { ok: false, error: embeddedRes.error };
    embeddedAssetSrcs = embeddedRes.pages.flatMap((doc) =>
      Object.values(doc.assetSrcById),
    );
    const titlesByDocId = Object.fromEntries(
      embeddedRes.pages.map((p) => [p.docDocumentId, p.title]),
    );
    const manifest = buildProposalManifest(selectedAlternate.pages, titlesByDocId);
    const { contact: senderContact } = resolveProposalContact({
      orgRow: args.proposalContactRow,
      brandNameFallback: args.brandName,
      organizationNameFallback: args.organizationName,
      formatOverride: formatContact.formatContact,
      useRepContact: args.opportunity.use_rep_proposal_contact === true,
      allowRepContactOverride: formatContact.allowRepContactOverride,
      profile: args.profile,
      location: args.activeLocation,
    });
    const formatEval = evaluateFormatCalcLines({
      lines: formatCalcLines,
      baseSubtotal: args.snapshot.subtotal_input ?? 0,
      valuesByLineId: args.snapshot.format_calc_values ?? {},
      modesByLineId: args.snapshot.format_calc_modes ?? {},
    });
    const depositAmount = args.snapshot.deposit_amount ?? 0;
    const totalSale = args.snapshot.total_sale_price ?? 0;
    const remainingBalance =
      args.snapshot.remaining_balance ?? Math.max(0, totalSale - depositAmount);
    const lineAmounts = buildPaymentLineAmountMap({
      baseAmount: args.snapshot.subtotal_input ?? 0,
      evaluatedCalcLines: [...formatEval.before, ...formatEval.after],
    });
    const paymentScheduleRows = evaluatePaymentMilestones({
      milestones: paymentMilestones,
      depositAmount,
      totalSalePrice: totalSale,
      remainingBalance,
      lineAmounts,
    });

    // Step 2: lazy-import the renderer + document (keeps the
    // @react-pdf/renderer chunk out of the main bundle). Must stay
    // inside try/catch — post-deploy stale hashes throw MIME/import
    // errors that previously froze the Save button.
    const [{ pdf }, { OpportunityProposalPdf }] = await Promise.all([
      import("@react-pdf/renderer"),
      import("./OpportunityProposalPdf"),
    ]);

    const document = OpportunityProposalPdf({
      brandName: args.brandName || args.organizationName,
      tagline: args.tagline,
      logoSrc,
      opportunity: args.opportunity,
      snapshot: args.snapshot,
      revisionNumber: args.revision?.revision_number ?? null,
      generatedAt: new Date(),
      proposalSections: resolved.sections,
      pageTitles: resolved.pageTitles,
      pageBlocks: resolved.pageBlocks,
      intentBlocks: resolved.intentBlocks,
      formatCalcVisibleLines: formatEval.proposalVisibleLines,
      formatPreSalesSubtotal: formatEval.totalBeforeTax,
      baseAmountLabel: formatSettings.base_amount_label,
      paymentScheduleRows,
      senderContact,
      manifest,
      embeddedDocPages: embeddedRes.pages,
      alternateKey: selectedAlternate.alternate_key,
      alternateLabel: selectedAlternate.label,
      archetype: args.archetype ?? "proposal",
    });
    const blob = await pdf(document).toBlob();
    return {
      ok: true,
      blob,
      alternateKey: selectedAlternate.alternate_key,
      alternateLabel: selectedAlternate.label,
    };
  } catch (err) {
    return { ok: false, error: pdfRenderErrorMessage(err) };
  } finally {
    if (logoSrc) revokeIfBlobUrl(logoSrc);
    for (const src of embeddedAssetSrcs) {
      if (src.startsWith("blob:")) revokeIfBlobUrl(src);
    }
  }
}

/**
 * Uploads an already-rendered document-package Blob via the sign+commit
 * flow and records the metadata row. Split out from
 * `renderProposalPdfToBlob` so the Preview modal can save the Blob it
 * already has without redoing the render.
 */
export async function uploadRenderedOpportunityDocumentPackage(args: {
  opportunity: Opportunity;
  revision: OpportunityRevision | null;
  blob: Blob;
  archetype?: OpportunityDocumentArchetype;
  alternateKey?: string | null;
  alternateLabel?: string | null;
}): Promise<GenerateProposalResult> {
  const archetype = args.archetype ?? "proposal";
  const uploaded = await uploadGeneratedProposalPdf({
    opportunityId: args.opportunity.id,
    revisionId: args.revision?.id ?? null,
    outputType: outputTypeForArchetype(archetype),
    blob: args.blob,
    alternateKey: args.alternateKey ?? null,
    alternateLabel: args.alternateLabel ?? null,
  });
  if (!uploaded.ok) return { ok: false, error: uploaded.error };
  return { ok: true, outputId: uploaded.output.id };
}

/** @deprecated Prefer `uploadRenderedOpportunityDocumentPackage`. */
export async function uploadRenderedProposalPdf(args: {
  opportunity: Opportunity;
  revision: OpportunityRevision | null;
  blob: Blob;
  alternateKey?: string | null;
  alternateLabel?: string | null;
}): Promise<GenerateProposalResult> {
  return uploadRenderedOpportunityDocumentPackage({ ...args, archetype: "proposal" });
}

/**
 * Convenience: render + upload in one call. Used by the "Save"
 * (skip-preview) button. The Preview flow does the two steps
 * separately so the user can abort between them.
 */
export async function generateAndUploadOpportunityDocumentPackage(
  args: GenerateProposalArgs,
): Promise<GenerateProposalResult> {
  const rendered = await renderOpportunityDocumentPackageToBlob(args);
  if (!rendered.ok) return { ok: false, error: rendered.error };
  return uploadRenderedOpportunityDocumentPackage({
    opportunity: args.opportunity,
    revision: args.revision,
    blob: rendered.blob,
    archetype: args.archetype ?? "proposal",
    alternateKey: rendered.alternateKey,
    alternateLabel: rendered.alternateLabel,
  });
}

/** @deprecated Prefer `generateAndUploadOpportunityDocumentPackage`. */
export async function generateAndUploadProposalPdf(
  args: GenerateProposalArgs,
): Promise<GenerateProposalResult> {
  return generateAndUploadOpportunityDocumentPackage({ ...args, archetype: "proposal" });
}

/**
 * Renders an opportunity document package (proposal or contract) to a
 * Blob without uploading. Proposal behavior is unchanged; contract
 * packages use the contract-archetype alternate selection.
 */
export async function renderOpportunityDocumentPackageToBlob(
  args: GenerateProposalArgs,
): Promise<RenderProposalResult> {
  return renderProposalPdfToBlob(args);
}

async function resolvePreviewPageAlternate(args: {
  formatKey: ReturnType<typeof resolveManagerPresetFormatKey>;
  productType: string;
  previewPageId?: string | null;
  fallback: ProposalAlternate;
}): Promise<{ ok: true; alternate: ProposalAlternate } | { ok: false; error: string }> {
  const pageId = args.previewPageId?.trim();
  if (!pageId) return { ok: true, alternate: args.fallback };

  const libraryRes = await listProposalPages({
    format_key: args.formatKey,
    product_type: args.productType,
    ensure: true,
  });
  if (!libraryRes.ok) return { ok: false, error: libraryRes.error };
  if (libraryRes.data.pages_supported === false) {
    return { ok: false, error: libraryRes.data.error ?? "Page library unavailable." };
  }
  const page = libraryRes.data.pages.find((p) => p.id === pageId);
  if (!page) return { ok: false, error: "Library page not found for preview." };

  return {
    ok: true,
    alternate: {
      ...args.fallback,
      alternate_key: `page:${page.id}`,
      label: page.label,
      pages: [
        {
          page_kind: page.archetype,
          sort_order: 0,
          included: true,
          proposal_page_id: page.id,
        },
      ],
    },
  };
}
